import { useMemo, useState } from 'react'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react'

import { useArAging } from './api'
import {
  CashflowV2Error,
  CashflowV2Loading,
  CashflowV2Unconfigured,
  looksUnconfigured,
} from './components/PageStates'
import { colors, fmt } from './utils'
import type { ArAgingRow } from './types'

type SortDir = 'asc' | 'desc'
type SortKey =
  | 'Business'
  | 'Type'
  | 'Num'
  | 'Name'
  | 'Terms'
  | 'Date'
  | 'Due Date'
  | 'Class'
  | 'Aging'
  | 'Open Balance'

interface AgingBucket {
  label: string
  subtitle: string
  min: number
  max: number
  color: string
  bgColor: string
}

const AGING_BUCKETS: AgingBucket[] = [
  {
    label: 'Current',
    subtitle: '0 days',
    min: -Infinity,
    max: 0,
    color: '#10b981',
    bgColor: 'rgba(16, 185, 129, 0.12)',
  },
  {
    label: '1-30',
    subtitle: '1-30 days',
    min: 1,
    max: 30,
    color: '#84cc16',
    bgColor: 'rgba(132, 204, 22, 0.12)',
  },
  {
    label: '31-60',
    subtitle: '31-60 days',
    min: 31,
    max: 60,
    color: '#f59e0b',
    bgColor: 'rgba(245, 158, 11, 0.12)',
  },
  {
    label: '61-90',
    subtitle: '61-90 days',
    min: 61,
    max: 90,
    color: '#f97316',
    bgColor: 'rgba(249, 115, 22, 0.12)',
  },
  {
    label: '91+',
    subtitle: '91+ days',
    min: 91,
    max: Infinity,
    color: '#ef4444',
    bgColor: 'rgba(239, 68, 68, 0.12)',
  },
]

const PAGE_SIZE = 25

function parseAging(raw: string): number {
  const numeric = Number(String(raw).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(numeric) ? numeric : 0
}

function openBalance(row: ArAgingRow): number {
  const value = row['Open Balance']
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function bucketForAging(aging: number): AgingBucket {
  if (aging <= 0) return AGING_BUCKETS[0]
  if (aging <= 30) return AGING_BUCKETS[1]
  if (aging <= 60) return AGING_BUCKETS[2]
  if (aging <= 90) return AGING_BUCKETS[3]
  return AGING_BUCKETS[4]
}

type RowView = ArAgingRow & { agingDays: number }

function toRowViews(rows: ArAgingRow[]): RowView[] {
  return rows.map(row => ({ ...row, agingDays: parseAging(row.Aging) }))
}

export default function ArCollectionsPage() {
  const arQuery = useArAging()
  const { data, isLoading, isError, error, refetch } = arQuery

  const [entityFilter, setEntityFilter] = useState('')
  const [bucketFilter, setBucketFilter] = useState('')
  const [classFilter, setClassFilter] = useState('')
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('Aging')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [page, setPage] = useState(1)

  const allRows = useMemo(() => toRowViews(data ?? []), [data])

  const entities = useMemo(() => {
    const values = new Set(allRows.map(row => row.Business))
    return ['', ...Array.from(values).sort()]
  }, [allRows])

  const classes = useMemo(() => {
    const values = new Set(allRows.map(row => row.Class))
    return ['', ...Array.from(values).sort()]
  }, [allRows])

  const bucketSummaries = useMemo(
    () =>
      AGING_BUCKETS.map(bucket => {
        const items = allRows.filter(
          row => row.agingDays >= bucket.min && row.agingDays <= bucket.max,
        )
        const total = items.reduce((sum, row) => sum + openBalance(row), 0)
        return { ...bucket, total, count: items.length }
      }),
    [allRows],
  )

  const filtered = useMemo(() => {
    let result = allRows
    if (entityFilter) result = result.filter(row => row.Business === entityFilter)
    if (bucketFilter) {
      const bucket = AGING_BUCKETS.find(value => value.label === bucketFilter)
      if (bucket) {
        result = result.filter(
          row => row.agingDays >= bucket.min && row.agingDays <= bucket.max,
        )
      }
    }
    if (classFilter) result = result.filter(row => row.Class === classFilter)
    if (search.trim()) {
      const query = search.toLowerCase()
      result = result.filter(row => row.Name.toLowerCase().includes(query))
    }
    return result
  }, [allRows, bucketFilter, classFilter, entityFilter, search])

  const sorted = useMemo(() => {
    const rows = [...filtered]
    rows.sort((a, b) => {
      let aVal: string | number = a[sortKey]
      let bVal: string | number = b[sortKey]

      if (sortKey === 'Open Balance') {
        aVal = openBalance(a)
        bVal = openBalance(b)
      } else if (sortKey === 'Aging') {
        aVal = a.agingDays
        bVal = b.agingDays
      } else if (sortKey === 'Num') {
        aVal = Number(String(a.Num).replace(/[^0-9.-]/g, '')) || 0
        bVal = Number(String(b.Num).replace(/[^0-9.-]/g, '')) || 0
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal
      }

      const aStr = String(aVal ?? '').toLowerCase()
      const bStr = String(bVal ?? '').toLowerCase()
      return sortDir === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
    })
    return rows
  }, [filtered, sortDir, sortKey])

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE)
  const safePage = totalPages === 0 ? 1 : Math.min(Math.max(1, page), totalPages)
  const paginated = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const entityBreakdown = useMemo(() => {
    const grouped = new Map<
      string,
      {
        current: number
        d1_30: number
        d31_60: number
        d61_90: number
        d91plus: number
        total: number
      }
    >()

    for (const row of allRows) {
      const existing =
        grouped.get(row.Business) ??
        { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d91plus: 0, total: 0 }
      const balance = openBalance(row)
      existing.total += balance
      if (row.agingDays <= 0) existing.current += balance
      else if (row.agingDays <= 30) existing.d1_30 += balance
      else if (row.agingDays <= 60) existing.d31_60 += balance
      else if (row.agingDays <= 90) existing.d61_90 += balance
      else existing.d91plus += balance
      grouped.set(row.Business, existing)
    }

    return Array.from(grouped.entries())
      .map(([name, value]) => ({ name, ...value }))
      .sort((a, b) => b.total - a.total)
  }, [allRows])

  const maxEntityTotal = useMemo(() => {
    if (entityBreakdown.length === 0) return 0
    return Math.max(...entityBreakdown.map(entity => entity.total))
  }, [entityBreakdown])

  const totalAR = useMemo(
    () => allRows.reduce((sum, row) => sum + openBalance(row), 0),
    [allRows],
  )

  function SortIcon({ column }: { column: SortKey }) {
    if (sortKey !== column) {
      return <ArrowUpDown size={12} className="text-gray-400 ml-1 inline" />
    }
    return sortDir === 'asc' ? (
      <ArrowUp size={12} className="ml-1 inline" style={{ color: colors.accent.teal }} />
    ) : (
      <ArrowDown size={12} className="ml-1 inline" style={{ color: colors.accent.teal }} />
    )
  }

  const tableColumns: { key: SortKey; label: string }[] = [
    { key: 'Business', label: 'Business' },
    { key: 'Type', label: 'Type' },
    { key: 'Num', label: 'Num' },
    { key: 'Name', label: 'Name' },
    { key: 'Terms', label: 'Terms' },
    { key: 'Date', label: 'Date' },
    { key: 'Due Date', label: 'Due Date' },
    { key: 'Class', label: 'Class' },
    { key: 'Aging', label: 'Aging' },
    { key: 'Open Balance', label: 'Open Balance' },
  ]

  if (isLoading) {
    return <CashflowV2Loading label="Loading AR collections…" />
  }

  if (isError) {
    if (looksUnconfigured(error)) {
      return <CashflowV2Unconfigured />
    }
    return (
      <CashflowV2Error
        title="Failed to load AR aging data"
        description={error instanceof Error ? error.message : undefined}
        onRetry={() => refetch()}
      />
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          AR Collections
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
          Invoice-level receivables aging analysis, {allRows.length} invoices,{' '}
          {fmt.compact(totalAR)} outstanding
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {bucketSummaries.map(bucket => (
          <button
            type="button"
            key={bucket.label}
            className="rounded-lg p-4 border text-left transition-colors hover:opacity-95"
            style={{
              backgroundColor: bucket.bgColor,
              borderColor: bucketFilter === bucket.label ? bucket.color : '#e5e7eb',
              borderWidth: bucketFilter === bucket.label ? 2 : 1,
            }}
            onClick={() => {
              setBucketFilter(bucketFilter === bucket.label ? '' : bucket.label)
              setPage(1)
            }}
          >
            <div className="flex items-center justify-between mb-1 gap-2">
              <span
                className="text-xs font-medium uppercase tracking-wide"
                style={{ color: bucket.color }}
              >
                {bucket.label}
              </span>
              <span
                className="text-[10px] font-mono px-1.5 py-0.5 rounded-full shrink-0"
                style={{ backgroundColor: `${bucket.color}22`, color: bucket.color }}
              >
                {bucket.count}
              </span>
            </div>
            <div
              className="font-mono text-lg font-semibold"
              style={{ color: bucket.color }}
            >
              {fmt.compact(bucket.total)}
            </div>
            <div className="text-[10px] text-gray-500 mt-1">{bucket.subtitle}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-4 shadow-sm">
        <div className="flex flex-col gap-1 min-w-[180px]">
          <label className="text-gray-400 dark:text-gray-500 text-[10px] uppercase tracking-wide">
            Business
          </label>
          <select
            value={entityFilter}
            onChange={event => {
              setEntityFilter(event.target.value)
              setPage(1)
            }}
            className="text-sm rounded-md px-3 py-1.5 border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface text-gray-900 dark:text-gray-100 outline-none"
          >
            <option value="">All businesses</option>
            {entities
              .filter(entity => entity !== '')
              .map(entity => (
                <option key={entity} value={entity}>
                  {entity}
                </option>
              ))}
          </select>
        </div>

        <div className="flex flex-col gap-1 min-w-[160px]">
          <label className="text-gray-400 dark:text-gray-500 text-[10px] uppercase tracking-wide">
            Aging Bucket
          </label>
          <select
            value={bucketFilter}
            onChange={event => {
              setBucketFilter(event.target.value)
              setPage(1)
            }}
            className="text-sm rounded-md px-3 py-1.5 border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface text-gray-900 dark:text-gray-100 outline-none"
          >
            <option value="">All buckets</option>
            {AGING_BUCKETS.map(bucket => (
              <option key={bucket.label} value={bucket.label}>
                {bucket.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1 min-w-[160px]">
          <label className="text-gray-400 dark:text-gray-500 text-[10px] uppercase tracking-wide">
            Class
          </label>
          <select
            value={classFilter}
            onChange={event => {
              setClassFilter(event.target.value)
              setPage(1)
            }}
            className="text-sm rounded-md px-3 py-1.5 border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface text-gray-900 dark:text-gray-100 outline-none"
          >
            <option value="">All classes</option>
            {classes
              .filter(value => value !== '')
              .map(value => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
          </select>
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-[220px] ml-auto">
          <label className="text-gray-400 dark:text-gray-500 text-[10px] uppercase tracking-wide">
            Search
          </label>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={event => {
                setSearch(event.target.value)
                setPage(1)
              }}
              placeholder="Customer name..."
              className="text-sm rounded-md pl-8 pr-3 py-1.5 w-full max-w-xs border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface text-gray-900 dark:text-gray-100 placeholder:text-gray-400 outline-none"
            />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-100 dark:border-dark-border p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-gray-900 dark:text-gray-100 text-base font-semibold">
            Invoices
            <span className="text-gray-400 dark:text-gray-500 text-sm font-normal ml-2">
              ({sorted.length} of {allRows.length})
            </span>
          </h2>
          <span className="text-gray-400 dark:text-gray-500 text-xs">
            Page {totalPages === 0 ? 0 : safePage} of {totalPages}
          </span>
        </div>

        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-200 dark:border-dark-border">
                {tableColumns.map(column => (
                  <th
                    key={column.key}
                    onClick={() => {
                      if (sortKey === column.key) {
                        setSortDir(direction => (direction === 'asc' ? 'desc' : 'asc'))
                      } else {
                        setSortKey(column.key)
                        setSortDir('desc')
                      }
                      setPage(1)
                    }}
                    className={`px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 transition-colors select-none whitespace-nowrap ${
                      column.key === 'Open Balance' ? 'text-right' : ''
                    }`}
                  >
                    {column.label}
                    <SortIcon column={column.key} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((row, index) => {
                const zebra =
                  index % 2 === 0
                    ? 'bg-white dark:bg-dark-surface'
                    : 'bg-gray-50/80 dark:bg-dark-surface-2/40'
                return (
                  <tr
                    key={`${row.Num}-${row.agingDays}-${index}`}
                    className={`border-b border-gray-100 dark:border-dark-border ${zebra} hover:bg-teal-50/40 dark:hover:bg-dark-surface-2/60 transition-colors`}
                  >
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-xs whitespace-nowrap">
                      {row.Business}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-xs whitespace-nowrap">
                      {row.Type}
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-900 dark:text-gray-100 text-xs whitespace-nowrap">
                      {row.Num}
                    </td>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100 min-w-[8rem]">
                      {row.Name}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-xs whitespace-nowrap">
                      {row.Terms}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {fmt.shortDate(row.Date)}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {fmt.shortDate(row['Due Date'])}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-xs">
                      {row.Class}
                    </td>
                    <td
                      className="px-3 py-2 font-mono text-xs font-semibold whitespace-nowrap"
                      style={{ color: bucketForAging(row.agingDays).color }}
                    >
                      {row.agingDays}d
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-900 dark:text-gray-100 text-right whitespace-nowrap">
                      {fmt.currency(openBalance(row))}
                    </td>
                  </tr>
                )
              })}
              {paginated.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    className="px-3 py-8 text-center text-gray-400 dark:text-gray-500"
                  >
                    No invoices match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && sorted.length > 0 && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-dark-border flex-wrap gap-3">
            <span className="text-gray-400 dark:text-gray-500 text-xs">
              Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, sorted.length)} of{' '}
              {sorted.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage(1)}
                disabled={safePage <= 1}
                className="px-2 py-1 text-xs rounded-md bg-gray-100 dark:bg-dark-surface-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                First
              </button>
              <button
                type="button"
                onClick={() => setPage(current => Math.max(1, current - 1))}
                disabled={safePage <= 1}
                className="p-1 rounded-md bg-gray-100 dark:bg-dark-surface-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() => setPage(current => Math.min(totalPages, current + 1))}
                disabled={safePage >= totalPages}
                className="p-1 rounded-md bg-gray-100 dark:bg-dark-surface-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
              <button
                type="button"
                onClick={() => setPage(totalPages)}
                disabled={safePage >= totalPages}
                className="px-2 py-1 text-xs rounded-md bg-gray-100 dark:bg-dark-surface-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-100 dark:border-dark-border p-5">
        <h2 className="text-gray-900 dark:text-gray-100 text-base font-semibold mb-4">
          Entity AR Breakdown
        </h2>
        <div className="space-y-3">
          {entityBreakdown.map(entity => {
            const widthPct = (value: number) =>
              maxEntityTotal > 0 ? (value / maxEntityTotal) * 100 : 0
            return (
              <div key={entity.name}>
                <div className="flex items-center justify-between mb-1 gap-2">
                  <span className="text-gray-900 dark:text-gray-100 text-sm truncate min-w-0">
                    {entity.name}
                  </span>
                  <span className="font-mono text-gray-500 dark:text-gray-400 text-xs shrink-0">
                    {fmt.compact(entity.total)}
                  </span>
                </div>
                <div className="flex h-5 rounded-md overflow-hidden bg-gray-100 dark:bg-dark-surface-2 ring-1 ring-gray-200/80 dark:ring-dark-border">
                  {entity.current > 0 && (
                    <div
                      className="h-full transition-all duration-300"
                      style={{
                        width: `${widthPct(entity.current)}%`,
                        backgroundColor: AGING_BUCKETS[0].color,
                      }}
                    />
                  )}
                  {entity.d1_30 > 0 && (
                    <div
                      className="h-full transition-all duration-300"
                      style={{
                        width: `${widthPct(entity.d1_30)}%`,
                        backgroundColor: AGING_BUCKETS[1].color,
                      }}
                    />
                  )}
                  {entity.d31_60 > 0 && (
                    <div
                      className="h-full transition-all duration-300"
                      style={{
                        width: `${widthPct(entity.d31_60)}%`,
                        backgroundColor: AGING_BUCKETS[2].color,
                      }}
                    />
                  )}
                  {entity.d61_90 > 0 && (
                    <div
                      className="h-full transition-all duration-300"
                      style={{
                        width: `${widthPct(entity.d61_90)}%`,
                        backgroundColor: AGING_BUCKETS[3].color,
                      }}
                    />
                  )}
                  {entity.d91plus > 0 && (
                    <div
                      className="h-full transition-all duration-300"
                      style={{
                        width: `${widthPct(entity.d91plus)}%`,
                        backgroundColor: AGING_BUCKETS[4].color,
                      }}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 pt-3 border-t border-gray-100 dark:border-dark-border">
          {AGING_BUCKETS.map(bucket => (
            <div key={bucket.label} className="flex items-center gap-1.5">
              <span
                className="w-3 h-3 rounded-sm shrink-0"
                style={{ backgroundColor: bucket.color }}
              />
              <span className="text-gray-400 dark:text-gray-500 text-xs">
                {bucket.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
